import React, { useEffect, useState } from "react";
import {
  Input,
  Select,
  Checkbox,
  Button,
  Card,
  Tag,
  Typography,
  Spin,
  Empty,
  Space,
} from "antd";
import BuilderWidget from "../../components/BuilderWidget";
import { useFetchApi } from "../../hooks/useFetchApi";
import { arrayToEquation } from "../../helper";
import DeleteModal from "./DeleteModal";
import { GRAPH_TYPES } from "../../store/types";

const { Title, Text } = Typography;

const EquationBuilder: React.FC = (): React.ReactElement => {
  const [equation, setEquation] = useState<Array<any>>([]);
  const [eId, setEId] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<any>([]);
  const [equationName, setEquationName] = useState<string>("");
  const [equationType, setEquationType] = useState<string>(
    GRAPH_TYPES.ABSOLUTE_VALUES,
  );
  const [category, setCategory] = useState<number>(1);
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [nameError, setNameError] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [deleteConfirmation, toggleDeleteConfirmation] =
    useState<boolean>(false);
  const [deleteData, setDeleteData] = useState<any>({});

  const {
    execute: getEquations,
    response: equations = [],
    loading: equationLoading,
  } = useFetchApi(`/api/equations`, {}, "GET");

  const {
    execute: getMetrics,
    response: m = [],
    loading: metricsLoading,
  } = useFetchApi(`/api/metrics`, {}, "GET");

  const {
    execute: addEquation,
    response: isEquationAddedOrUpdated = [],
    loading: addEquationLoading,
  } = useFetchApi(`/api/equations`, {}, eId ? "PUT" : "POST");

  const { execute: fetchMetricCategories, response: metricCategories } =
    useFetchApi("/api/metrics/categories", {}, "GET");

  const handleSubmit = (equation: any, id?: string | null) => {
    if (!equationName.trim()) {
      setNameError(true);
      return;
    }
    addEquation({
      id: id ? id : undefined,
      equation: JSON.stringify(equation),
      name: equationName,
      type: equationType,
      metric_category_id: category,
      is_visible: isVisible ? 1 : 0,
    });
  };

  const handleEdit = (
    e: any,
    equation: any,
    id: string,
    name: string,
    type: string,
    metric_category_id: number,
    is_visible: boolean,
  ) => {
    setEId(id);
    setEquation(JSON.parse(equation));
    setEquationName(name);
    setEquationType(type);
    console.log("metric_category_id", metric_category_id);
    setCategory(metric_category_id);
    setIsVisible(!!is_visible);
  };

  const handleChange = (e: any) => {
    setEquationName(e.target.value);
    if (e.target.value.trim()) {
      setNameError(false);
    }
  };

  const handleEquationTypeChange = (value: string) => {
    console.log("onChange", value);
    setEquationType(value);
  };

  const handleMetricCategory = (value: number) => {
    setCategory(value);
  };

  const handleIsVisibleChange = (e: any) => {
    setIsVisible(e.target.checked);
  };

  const handleSearchChange = (e: any) => {
    setSearchQuery(e.target.value);
  };

  useEffect(() => {
    getEquations();
    getMetrics();
    fetchMetricCategories();
  }, []);

  useEffect(() => {
    if (equations && !equationLoading && m && !metricsLoading) {
      setMetrics([...equations.map((e: any) => e.name), ...m]);
    }
  }, [equations, equationLoading, m, metricsLoading]);

  useEffect(() => {
    if (isEquationAddedOrUpdated) {
      setEquation([]);
      setEId(null);
      getEquations();
      setEquationName("");
      setEquationType(GRAPH_TYPES.ABSOLUTE_VALUES);
      setIsVisible(true);
      setNameError(false);
    }
  }, [isEquationAddedOrUpdated]);

  const filteredEquations =
    equations && equations.length
      ? equations.filter((eq: any) =>
          (eq.name || "")
            .toLowerCase()
            .includes(searchQuery.trim().toLowerCase()),
        )
      : equations;

  return (
    <div className="flex w-full p-8 bg-white min-h-full">
      <Spin
        spinning={equationLoading || metricsLoading || addEquationLoading}
        fullscreen
      />
      <DeleteModal
        data={deleteData}
        id="equation-delete-model"
        status={deleteConfirmation}
        onClose={() => toggleDeleteConfirmation(false)}
        onSuccess={getEquations}
      />
      <div className="flex flex-col w-6/12 p-4">
        <div className="flex justify-between mb-8">
          <Title level={4}>Equation Builder</Title>
        </div>
        <div className="flex w-full flex-col">
          <div className="flex flex-col mb-4">
            <Text className="mb-2 block">
              Equation Name <Text type="danger">*</Text>
            </Text>
            <Input
              id="equation-name"
              value={equationName}
              onChange={handleChange}
              status={nameError ? "error" : undefined}
            />
            {nameError ? (
              <Text type="danger" className="mt-1 block">
                Equation name is required
              </Text>
            ) : null}
          </div>
          <div className="flex flex-col mb-4">
            <Text className="mb-2 block">Equation Type</Text>
            <Select
              className="w-full"
              value={equationType}
              onChange={handleEquationTypeChange}
              options={[
                {
                  value: GRAPH_TYPES.ABSOLUTE_VALUES,
                  label: "Absolute Values",
                },
                { value: GRAPH_TYPES.PERCENTAGES, label: "Percentages" },
                { value: GRAPH_TYPES.MULTIPLES, label: "Multiples" },
                { value: GRAPH_TYPES.DAYS, label: "Days" },
              ]}
            />
          </div>
          {metricCategories && metricCategories.length ? (
            <div className="flex flex-col mb-4">
              <Text className="mb-2 block">Equation Category</Text>
              <Select
                className="w-full"
                value={category}
                onChange={handleMetricCategory}
                options={metricCategories.map((item: any) => ({
                  value: item.id,
                  label: item.name,
                }))}
              />
            </div>
          ) : null}
          <div className="mb-4">
            <Checkbox checked={isVisible} onChange={handleIsVisibleChange}>
              Is Visible
            </Checkbox>
          </div>
          <BuilderWidget
            onSubmit={handleSubmit}
            equation={equation}
            id={eId}
            m={metrics}
          />
        </div>
      </div>
      <div className="flex flex-col w-6/12 p-4">
        <div className="flex mb-4">
          <Title level={4}>All Equations</Title>
        </div>
        <div className="mb-4">
          <Input.Search
            placeholder="Search by equation name"
            value={searchQuery}
            onChange={handleSearchChange}
            allowClear
          />
        </div>
        <div className="flex flex-col w-full max-h-[660px] overflow-y-auto">
          {filteredEquations && filteredEquations.length ? (
            <>
              {filteredEquations.map(
                ({
                  id,
                  name,
                  equation,
                  status,
                  type,
                  metric_category_name,
                  metric_category_id,
                  is_visible,
                }: any) => (
                  <Card
                    key={id}
                    size="small"
                    hoverable
                    className="mb-3 shadow-md border border-gray-200 rounded-lg"
                  >
                    <Space direction="vertical" className="w-full" size="small">
                      <Text className="!mb-0">
                        <Text strong>Name:</Text> {name}
                      </Text>
                      <Text code className="break-all block p-2">
                        Equation: {arrayToEquation(JSON.parse(equation))}
                      </Text>
                      <Space wrap>
                        <Tag color={status ? "success" : "error"}>
                          Status: {status ? "Active" : "Inactive"}
                        </Tag>
                        <Tag color="blue">Equation Type: {type}</Tag>
                        <Tag color="geekblue">
                          Equation Category: {metric_category_name}
                        </Tag>
                        <Tag color={is_visible ? "success" : "error"}>
                          {is_visible ? "Visible" : "Hidden"}
                        </Tag>
                      </Space>
                      <Space>
                        <Button
                          id="newEdit"
                          type="primary"
                          size="small"
                          onClick={(e: any) =>
                            handleEdit(
                              e,
                              equation,
                              id,
                              name,
                              type,
                              metric_category_id,
                              is_visible,
                            )
                          }
                        >
                          Edit
                        </Button>
                        <Button
                          id="delete"
                          danger
                          size="small"
                          onClick={(e: any) => {
                            setDeleteData({ id });
                            toggleDeleteConfirmation(!deleteConfirmation);
                          }}
                        >
                          Delete
                        </Button>
                      </Space>
                    </Space>
                  </Card>
                ),
              )}
            </>
          ) : (
            <div className="flex justify-center p-4">
              <Empty
                description={
                  searchQuery.trim()
                    ? "No equations match your search"
                    : "No data available yet"
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EquationBuilder;
